import { AgentClient } from 'agents/client';
import { vueEnv } from '@/globals';
import {
  parseStreamBody,
  extractPageBuilderEvents,
  isPageStartEvent,
  isPageStatusEvent,
  isPageOperationEvent,
} from '@/lib/aiStreamProtocol';
import api from '@/factories/api';
import Agents from '@/store/resource/agents';

// Backend resource for persisting agents
const agentsResource = new Agents();

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

// Build a unique key for storing agent instances
// Format: "agent:name" (e.g., "customer-support:default" or "page-builder:page-123")
function buildKey(agent, name = 'default') {
  return `${agent}:${name}`;
}

const state = {
  // All state is keyed by "${agent}:${name}" for multi-instance support
  connections: {}, // { [key]: AgentClient instance }
  connectionStatus: {}, // { [key]: 'disconnected' | 'connecting' | 'connected' }
  messages: {}, // { [key]: Message[] }
  agentStates: {}, // { [key]: agent's synced state }
  loading: {}, // { [key]: boolean }
  currentRequestIds: {}, // { [key]: string } for cancellation
  currentMessageId: {}, // { [key]: string } - tracks message ID being streamed

  // Back-channel data events (page builder events, etc.) - separate from messages
  dataEvents: {}, // { [key]: DataEvent[] } - events that don't appear in chat
  activePageBuild: {}, // { [key]: { pageId, status, description, error?, operationCount? } }

  // Instance registry for tracking conversation metadata
  // { [key]: { displayName, agentType, instanceName, createdAt } }
  instanceRegistry: {},

  // JWT token for agent connections
  agentsJwtToken: null,
  agentsJwtTokenRefreshAt: null, // Unix timestamp (seconds)

  // Backend-persisted agents from ConveYour API
  // Array of agent records: { id, name, type, uuid, created_at, updated_at }
  backendAgents: [],
  backendAgentsLoading: false,
  backendAgentsLoaded: false,
};

const mutations = {
  setConnection(state, { key, client }) {
    state.connections = { ...state.connections, [key]: client };
  },

  setConnectionStatus(state, { key, status }) {
    state.connectionStatus = { ...state.connectionStatus, [key]: status };
  },

  setMessages(state, { key, messages }) {
    state.messages = { ...state.messages, [key]: messages };
  },

  addMessage(state, { key, message }) {
    const currentMessages = state.messages[key] || [];
    state.messages = {
      ...state.messages,
      [key]: [...currentMessages, message],
    };
  },

  appendToLastMessage(state, { key, chunk }) {
    const messages = state.messages[key];
    if (!messages || messages.length === 0) return;

    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === 'assistant') {
      // Create a new array with the updated last message for reactivity
      const updatedMessages = messages.slice(0, -1).concat({
        ...lastMsg,
        content: lastMsg.content + chunk,
      });
      state.messages = { ...state.messages, [key]: updatedMessages };
    }
  },

  setAgentState(state, { key, agentState }) {
    state.agentStates = { ...state.agentStates, [key]: agentState };
  },

  setLoading(state, { key, loading }) {
    state.loading = { ...state.loading, [key]: loading };
  },

  setCurrentRequestId(state, { key, requestId }) {
    state.currentRequestIds = { ...state.currentRequestIds, [key]: requestId };
  },

  clearAgent(state, { key }) {
    const { [key]: _conn, ...restConnections } = state.connections;
    const { [key]: _status, ...restStatus } = state.connectionStatus;
    const { [key]: _msgs, ...restMessages } = state.messages;
    const { [key]: _agentState, ...restAgentStates } = state.agentStates;
    const { [key]: _loading, ...restLoading } = state.loading;
    const { [key]: _reqId, ...restRequestIds } = state.currentRequestIds;
    const { [key]: _msgId, ...restMessageIds } = state.currentMessageId;
    const { [key]: _events, ...restDataEvents } = state.dataEvents;
    const { [key]: _build, ...restPageBuilds } = state.activePageBuild;
    const { [key]: _instance, ...restRegistry } = state.instanceRegistry;

    state.connections = restConnections;
    state.connectionStatus = restStatus;
    state.messages = restMessages;
    state.agentStates = restAgentStates;
    state.loading = restLoading;
    state.currentRequestIds = restRequestIds;
    state.currentMessageId = restMessageIds;
    state.dataEvents = restDataEvents;
    state.activePageBuild = restPageBuilds;
    state.instanceRegistry = restRegistry;
  },

  // Stream protocol mutations

  setCurrentMessageId(state, { key, messageId }) {
    state.currentMessageId = { ...state.currentMessageId, [key]: messageId };
  },

  setAgentsJwtToken(state, { token, refreshAt }) {
    state.agentsJwtToken = token;
    state.agentsJwtTokenRefreshAt = refreshAt;
  },

  /**
   * Start a new assistant message with the standardized format
   * Called when receiving a message_start (f:) protocol item
   */
  startAssistantMessage(state, { key, messageId }) {
    const messages = state.messages[key] || [];
    const lastMsg = messages[messages.length - 1];

    // If there's already a pending assistant message, update it with the real ID
    if (
      lastMsg &&
      lastMsg.role === 'assistant' &&
      lastMsg.status === 'pending'
    ) {
      const updatedMessages = messages.slice(0, -1).concat({
        ...lastMsg,
        id: messageId,
        status: 'streaming',
      });
      state.messages = { ...state.messages, [key]: updatedMessages };
    } else {
      // Starting a new message means any previous assistant message's tools are done
      // Mark all pending tool calls in previous messages as complete
      const updatedMessages = messages.map((msg) => {
        if (
          msg.role === 'assistant' &&
          msg.toolCalls?.some((tc) => tc.status === 'pending')
        ) {
          return {
            ...msg,
            status: msg.status === 'streaming' ? 'complete' : msg.status,
            toolCalls: msg.toolCalls.map((tc) =>
              tc.status === 'pending' ? { ...tc, status: 'complete' } : tc
            ),
          };
        }
        return msg;
      });

      // Create new assistant message
      const newMessage = {
        id: messageId,
        role: 'assistant',
        content: '',
        status: 'streaming',
        toolCalls: [],
        error: null,
        metadata: null,
      };
      state.messages = {
        ...state.messages,
        [key]: [...updatedMessages, newMessage],
      };
    }

    state.currentMessageId = { ...state.currentMessageId, [key]: messageId };
  },

  /**
   * Append a text chunk to the current streaming message
   * Called when receiving text (0:) protocol items
   */
  appendTextChunk(state, { key, text }) {
    const messages = state.messages[key];
    if (!messages || messages.length === 0) return;

    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === 'assistant') {
      const updatedMessages = messages.slice(0, -1).concat({
        ...lastMsg,
        content: lastMsg.content + text,
      });
      state.messages = { ...state.messages, [key]: updatedMessages };
    }
  },

  /**
   * Add a tool call to the current message
   * Called when receiving tool_call (9:) protocol items
   */
  addToolCall(state, { key, toolCall }) {
    const messages = state.messages[key];
    if (!messages || messages.length === 0) return;

    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === 'assistant') {
      const updatedToolCalls = [...(lastMsg.toolCalls || []), toolCall];
      const updatedMessages = messages.slice(0, -1).concat({
        ...lastMsg,
        toolCalls: updatedToolCalls,
      });
      state.messages = { ...state.messages, [key]: updatedMessages };
    }
  },

  /**
   * Update a tool call with its result
   * Called when receiving tool_result (b:) protocol items
   * Searches through ALL messages to find the tool call by ID
   */
  setToolResult(state, { key, toolCallId, result }) {
    const messages = state.messages[key];
    if (!messages || messages.length === 0) return;

    // Search all messages for the tool call (it may not be in the last message)
    const updatedMessages = messages.map((msg) => {
      if (
        msg.role === 'assistant' &&
        msg.toolCalls?.some((tc) => tc.id === toolCallId)
      ) {
        return {
          ...msg,
          toolCalls: msg.toolCalls.map((tc) =>
            tc.id === toolCallId ? { ...tc, status: 'complete', result } : tc
          ),
        };
      }
      return msg;
    });

    state.messages = { ...state.messages, [key]: updatedMessages };
  },

  /**
   * Set an error on the current message
   * Called when receiving error (3:) protocol items
   */
  setMessageError(state, { key, error }) {
    const messages = state.messages[key];
    if (!messages || messages.length === 0) return;

    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === 'assistant') {
      const updatedMessages = messages.slice(0, -1).concat({
        ...lastMsg,
        status: 'error',
        error,
      });
      state.messages = { ...state.messages, [key]: updatedMessages };
    }
  },

  /**
   * Mark the current message as complete with metadata
   * Called when receiving finish_message (d:) protocol items
   */
  completeMessage(state, { key, metadata }) {
    const messages = state.messages[key];
    if (!messages || messages.length === 0) return;

    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === 'assistant') {
      const updatedMessages = messages.slice(0, -1).concat({
        ...lastMsg,
        status: 'complete',
        metadata,
      });
      state.messages = { ...state.messages, [key]: updatedMessages };
    }

    // Clear current message ID
    const { [key]: _msgId, ...restMessageIds } = state.currentMessageId;
    state.currentMessageId = restMessageIds;
  },

  /**
   * Mark all pending tool calls in the current message as complete
   * Called when receiving finish_step (e:) protocol items
   * This signals a step in a multi-step flow has completed (e.g., tool finished)
   */
  completeCurrentStep(state, { key }) {
    const messages = state.messages[key];
    if (!messages || messages.length === 0) return;

    const lastMsg = messages[messages.length - 1];
    if (
      lastMsg &&
      lastMsg.role === 'assistant' &&
      lastMsg.toolCalls?.length > 0
    ) {
      const updatedToolCalls = lastMsg.toolCalls.map((tc) =>
        tc.status === 'pending' ? { ...tc, status: 'complete' } : tc
      );
      const updatedMessages = messages.slice(0, -1).concat({
        ...lastMsg,
        toolCalls: updatedToolCalls,
      });
      state.messages = { ...state.messages, [key]: updatedMessages };
    }
  },

  // ============================================================================
  // Feedback Mutations
  // ============================================================================

  setMessageFeedback(state, { key, messageId, feedback }) {
    const messages = state.messages[key];
    if (!messages) return;

    const updatedMessages = messages.map((msg) =>
      msg.id === messageId ? { ...msg, feedback } : msg
    );
    state.messages = { ...state.messages, [key]: updatedMessages };
  },

  // ============================================================================
  // Data Events / Page Builder Mutations
  // ============================================================================

  /**
   * Add a data event to the back-channel events array
   * These events are not displayed in the chat but can be consumed by other components
   */
  addDataEvent(state, { key, event }) {
    const currentEvents = state.dataEvents[key] || [];
    state.dataEvents = {
      ...state.dataEvents,
      [key]: [...currentEvents, event],
    };
  },

  /**
   * Initialize or update an active page build session
   * Called when receiving a pageStart event
   */
  setActivePageBuild(state, { key, pageId, description }) {
    state.activePageBuild = {
      ...state.activePageBuild,
      [key]: {
        pageId,
        description,
        status: 'building',
        error: null,
        operationCount: 0,
      },
    };
  },

  /**
   * Update the status of an active page build
   * Called when receiving pageStatus events
   */
  updatePageBuildStatus(state, { key, pageId, status, error, operationCount }) {
    const currentBuild = state.activePageBuild[key];
    if (!currentBuild || currentBuild.pageId !== pageId) return;

    state.activePageBuild = {
      ...state.activePageBuild,
      [key]: {
        ...currentBuild,
        status,
        error: error !== undefined ? error : currentBuild.error,
        operationCount:
          operationCount !== undefined
            ? operationCount
            : currentBuild.operationCount,
      },
    };
  },

  /**
   * Increment the operation count for the active page build
   * Called when receiving pageOperation events
   */
  incrementPageOperationCount(state, { key }) {
    const currentBuild = state.activePageBuild[key];
    if (!currentBuild) return;

    state.activePageBuild = {
      ...state.activePageBuild,
      [key]: {
        ...currentBuild,
        operationCount: (currentBuild.operationCount || 0) + 1,
      },
    };
  },

  /**
   * Clear all data events for an agent
   */
  clearDataEvents(state, { key }) {
    const { [key]: _events, ...restEvents } = state.dataEvents;
    state.dataEvents = restEvents;
  },

  /**
   * Clear the active page build for an agent
   */
  clearActivePageBuild(state, { key }) {
    const { [key]: _build, ...restBuilds } = state.activePageBuild;
    state.activePageBuild = restBuilds;
  },

  // ============================================================================
  // Instance Registry Mutations
  // ============================================================================

  /**
   * Register a new agent instance with metadata
   * @param {string} key - The agent key (agent:name)
   * @param {string} agentType - The agent type (e.g., 'chat', 'page-builder')
   * @param {string} instanceName - The unique instance name (UUID)
   * @param {string} [displayName] - Optional display name for the conversation
   */
  registerInstance(state, { key, agentType, instanceName, displayName = '' }) {
    // Don't overwrite if already registered
    if (state.instanceRegistry[key]) return;

    state.instanceRegistry = {
      ...state.instanceRegistry,
      [key]: {
        displayName,
        agentType,
        instanceName,
        createdAt: Date.now(),
      },
    };
  },

  /**
   * Update the display name for an instance
   * Typically called after the first user message to set a meaningful name
   */
  updateInstanceDisplayName(state, { key, displayName }) {
    const instance = state.instanceRegistry[key];
    if (!instance) return;

    state.instanceRegistry = {
      ...state.instanceRegistry,
      [key]: {
        ...instance,
        displayName,
      },
    };
  },

  /**
   * Remove an instance from the registry
   */
  unregisterInstance(state, { key }) {
    const { [key]: _instance, ...rest } = state.instanceRegistry;
    state.instanceRegistry = rest;
  },

  // ============================================================================
  // Backend Agents Mutations
  // ============================================================================

  /**
   * Set all backend agents (replaces the array)
   */
  setBackendAgents(state, agents) {
    state.backendAgents = agents;
  },

  /**
   * Add a single agent to the backend agents list
   */
  addBackendAgent(state, agent) {
    // Check if agent already exists by uuid
    const existingIndex = state.backendAgents.findIndex(
      (a) => a.uuid === agent.uuid
    );
    if (existingIndex >= 0) {
      // Replace existing agent
      state.backendAgents = [
        ...state.backendAgents.slice(0, existingIndex),
        agent,
        ...state.backendAgents.slice(existingIndex + 1),
      ];
    } else {
      // Prepend new agent (newest first)
      state.backendAgents = [agent, ...state.backendAgents];
    }
  },

  /**
   * Update a backend agent by id
   */
  updateBackendAgent(state, agent) {
    const index = state.backendAgents.findIndex((a) => a.id === agent.id);
    if (index >= 0) {
      state.backendAgents = [
        ...state.backendAgents.slice(0, index),
        { ...state.backendAgents[index], ...agent },
        ...state.backendAgents.slice(index + 1),
      ];
    }
  },

  /**
   * Remove a backend agent by id
   */
  removeBackendAgent(state, agentId) {
    state.backendAgents = state.backendAgents.filter((a) => a.id !== agentId);
  },

  /**
   * Set loading state for backend agents
   */
  setBackendAgentsLoading(state, loading) {
    state.backendAgentsLoading = loading;
  },

  /**
   * Set loaded state for backend agents
   */
  setBackendAgentsLoaded(state, loaded) {
    state.backendAgentsLoaded = loaded;
  },
};

const actions = {
  /**
   * Ensure we have a valid JWT token for agent connections.
   * Fetches a new token if none exists or if refresh_at has passed.
   * @returns {Promise<string>} The valid JWT token
   */
  async ensureAgentsToken({ state, commit }) {
    const now = Math.floor(Date.now() / 1000); // Current time in seconds

    // Check if we have a valid token that doesn't need refresh
    if (
      state.agentsJwtToken &&
      state.agentsJwtTokenRefreshAt &&
      state.agentsJwtTokenRefreshAt > now
    ) {
      return state.agentsJwtToken;
    }

    // Fetch a new token
    const response = await api().post('users/ai_agents_token');
    const { token, refresh_at } = response;

    commit('setAgentsJwtToken', { token, refreshAt: refresh_at });

    return token;
  },

  /**
   * Connect to an agent
   * @param {string} agent - The agent type (e.g., 'customer-support', 'page-builder')
   * @param {string} [name='default'] - Unique instance name (e.g., 'default', 'page-123', 'user-456')
   * @param {string} [host] - Optional override for agents host (defaults to VUE_APP_AGENTS_HOST)
   * @param {string} [token] - Optional override for auth token (defaults to agents JWT token)
   * @param {string} [displayName] - Optional display name for the agent (used when persisting)
   * @param {boolean} [persist=true] - Whether to persist the agent to the backend
   */
  async connect(
    { commit, dispatch, state },
    { agent, name = 'default', host, token, displayName = '', persist = true }
  ) {
    const key = buildKey(agent, name);

    // Don't reconnect if already connected or connecting
    const currentStatus = state.connectionStatus[key];
    if (currentStatus === 'connected' || currentStatus === 'connecting') {
      return;
    }

    // Get host from env if not provided
    const resolvedHost = host || vueEnv('agents_host');
    if (!resolvedHost) {
      console.error(
        'agents/connect: No host provided and VUE_APP_AGENTS_HOST is not set'
      );
      return;
    }

    // Ensure we have a valid token before connecting
    const resolvedToken =
      token !== undefined ? token : await dispatch('ensureAgentsToken');

    commit('setConnectionStatus', { key, status: 'connecting' });

    const client = new AgentClient({
      agent,
      name,
      host: resolvedHost,
      query: resolvedToken ? { token: resolvedToken } : undefined,
      onStateUpdate: (agentState) => {
        commit('setAgentState', { key, agentState });
      },
    });

    client.addEventListener('open', async () => {
      commit('setConnectionStatus', { key, status: 'connected' });

      // Persist agent to backend on successful connection
      // Only persist if this is a new agent (not already in backend)
      if (persist) {
        const existingAgent = state.backendAgents.find((a) => a.uuid === name);
        if (!existingAgent) {
          try {
            await dispatch('createBackendAgent', {
              name: displayName || 'New conversation',
              type: agent,
              uuid: name,
            });
          } catch (err) {
            // Log but don't fail the connection if backend persist fails
            console.warn('Failed to persist agent to backend:', err);
          }
        }
      }
    });

    client.addEventListener('close', () => {
      commit('setConnectionStatus', { key, status: 'disconnected' });
    });

    client.addEventListener('error', () => {
      commit('setConnectionStatus', { key, status: 'disconnected' });
    });

    client.addEventListener('message', (event) => {
      dispatch('handleMessage', { key, rawData: event.data });
    });

    commit('setConnection', { key, client });
  },

  disconnect({ state, commit }, { agent, name = 'default' }) {
    const key = buildKey(agent, name);
    const client = state.connections[key];
    if (client) {
      client.close();
    }
    commit('clearAgent', { key });
  },

  handleMessage({ commit, dispatch }, { key, rawData }) {
    let data;
    try {
      data = JSON.parse(rawData);
    } catch (e) {
      return;
    }

    switch (data.type) {
      case 'cf_agent_state':
        commit('setAgentState', { key, agentState: data.state });
        break;

      case 'cf_agent_chat_messages':
        // Normalize incoming messages to ensure they have the standardized format
        const normalizedMessages = (data.messages || []).map((msg) => ({
          id: msg.id || generateId(),
          role: msg.role,
          content: msg.content || '',
          status: msg.status || 'complete',
          toolCalls: msg.toolCalls || [],
          error: msg.error || null,
          metadata: msg.metadata || null,
          feedback: msg.feedback || null,
          turnId: msg.turnId || null,
        }));
        commit('setMessages', { key, messages: normalizedMessages });
        break;

      case 'cf_agent_use_chat_response':
        // Parse the Vercel AI SDK stream protocol
        if (data.body) {
          const items = parseStreamBody(data.body);
          for (const item of items) {
            dispatch('handleStreamItem', { key, item });
          }
        }
        if (data.done) {
          commit('setLoading', { key, loading: false });
        }
        break;

      case 'cf_agent_chat_clear':
        commit('setMessages', { key, messages: [] });
        break;

      case 'cf_agent_feedback_ack':
        // Server confirmed feedback was saved - update local state
        commit('setMessageFeedback', {
          key,
          messageId: data.messageId,
          feedback: data.feedback,
        });
        break;
    }
  },

  /**
   * Handle a single parsed stream protocol item
   * Routes each item type to the appropriate mutation
   */
  handleStreamItem({ commit, dispatch }, { key, item }) {
    switch (item.type) {
      case 'message_start':
        // New message is starting with a given ID
        commit('startAssistantMessage', {
          key,
          messageId: item.data.messageId,
        });
        break;

      case 'text':
        // Text token from the model - append to current message
        commit('appendTextChunk', { key, text: item.data });
        break;

      case 'tool_call':
        // Model is invoking a tool
        commit('addToolCall', {
          key,
          toolCall: {
            id: item.data.toolCallId,
            name: item.data.toolName,
            args: item.data.args,
            status: 'pending',
            result: null,
          },
        });
        break;

      case 'tool_result':
        // Tool returned a result
        commit('setToolResult', {
          key,
          toolCallId: item.data.toolCallId,
          result: item.data.result,
        });
        break;

      case 'error':
        // Error occurred during streaming
        commit('setMessageError', { key, error: item.data });
        break;

      case 'finish_message':
        // Message generation complete with metadata
        commit('completeMessage', { key, metadata: item.data });
        break;

      case 'finish_step':
        // A step in multi-step flow completed (e.g., tool call finished)
        // Mark all pending tool calls in the current message as complete
        commit('completeCurrentStep', { key });
        break;

      case 'tool_call_streaming_start':
      case 'tool_call_delta':
        // Streaming tool calls - currently not needed for basic use
        break;

      case 'data':
        // Data events are the back-channel for page builder and other non-chat events
        dispatch('handleDataEvent', { key, item });
        break;

      case 'message_annotation':
        // Message annotations - currently not needed for basic use
        break;

      default:
        // Unknown type - log for debugging
        console.log('Unhandled stream item type:', item.type, item);
    }
  },

  /**
   * Handle data events from the stream (back-channel events)
   * Routes page builder events to appropriate handlers and stores them
   */
  handleDataEvent({ commit, dispatch }, { key, item }) {
    // Extract page builder events from the data item
    const pageEvents = extractPageBuilderEvents(item);

    for (const event of pageEvents) {
      // Store every page builder event for subscribers to consume
      commit('addDataEvent', { key, event });

      if (isPageStartEvent(event)) {
        // Initialize a new page build session
        commit('setActivePageBuild', {
          key,
          pageId: event.pageId,
          description: event.description,
        });
      } else if (isPageStatusEvent(event)) {
        // Update page build status
        commit('updatePageBuildStatus', {
          key,
          pageId: event.pageId,
          status: event.status,
          error: event.error,
          operationCount: event.operationCount,
        });
      } else if (isPageOperationEvent(event)) {
        // Track operation count
        commit('incrementPageOperationCount', { key });

        // Bridge to pages store for real-time rendering
        dispatch('pages/applyUpdate', event.operation, { root: true });
      }
    }
  },

  /**
   * Send a backchannel context update to the agent.
   * Context is invisible to the user and injected into the system prompt.
   * @param {string} agent - The agent type
   * @param {string} [name='default'] - Instance name
   * @param {Object} context - The context payload
   */
  updateContext({ state }, { agent, name = 'default', context }) {
    const key = buildKey(agent, name);
    const client = state.connections[key];
    if (!client) {
      console.warn('agents/updateContext: agent not connected');
      return;
    }

    client.send(
      JSON.stringify({
        type: 'cf_agent_context_update',
        context,
      })
    );
  },

  sendMessage({ state, commit }, { agent, name = 'default', content }) {
    const key = buildKey(agent, name);
    const client = state.connections[key];
    if (!client) return;

    // Create user message with standardized format
    const userMessage = {
      id: generateId(),
      role: 'user',
      content,
      status: 'complete',
      toolCalls: [],
      error: null,
      metadata: null,
    };

    commit('addMessage', { key, message: userMessage });

    // Create placeholder assistant message with standardized format
    // The real ID will be set when we receive a message_start (f:) from the stream
    commit('addMessage', {
      key,
      message: {
        id: generateId(),
        role: 'assistant',
        content: '',
        status: 'pending',
        toolCalls: [],
        error: null,
        metadata: null,
      },
    });
    commit('setLoading', { key, loading: true });

    const requestId = generateId();
    commit('setCurrentRequestId', { key, requestId });

    // Get all messages except the empty assistant placeholder
    const messagesToSend = state.messages[key].slice(0, -1);

    client.send(
      JSON.stringify({
        type: 'cf_agent_use_chat_request',
        id: requestId,
        url: '/api/chat',
        init: {
          method: 'POST',
          body: JSON.stringify({
            messages: messagesToSend,
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      })
    );
  },

  cancelRequest({ state, commit }, { agent, name = 'default' }) {
    const key = buildKey(agent, name);
    const client = state.connections[key];
    const requestId = state.currentRequestIds[key];
    if (!client || !requestId) return;

    client.send(
      JSON.stringify({
        type: 'cf_agent_chat_request_cancel',
        id: requestId,
      })
    );

    commit('setLoading', { key, loading: false });
  },

  clearHistory({ state, commit }, { agent, name = 'default' }) {
    const key = buildKey(agent, name);
    const client = state.connections[key];
    if (client) {
      client.send(
        JSON.stringify({
          type: 'cf_agent_chat_clear',
        })
      );
    }
    commit('setMessages', { key, messages: [] });
  },

  /**
   * Dismiss/clear the active page builder for an agent
   * Clears both the active build state and the data events
   */
  dismissPageBuilder({ commit }, { agent, name = 'default' }) {
    const key = buildKey(agent, name);
    commit('clearActivePageBuild', { key });
    commit('clearDataEvents', { key });
  },

  /**
   * Submit feedback for a specific message (turn-level feedback)
   * Sends via WebSocket and optimistically updates the UI
   * @param {string} agent - The agent type
   * @param {string} name - The instance name
   * @param {string} messageId - The message to rate
   * @param {string} turnId - The turn ID (for analytics)
   * @param {'positive' | 'negative'} rating - The feedback rating
   */
  submitFeedback(
    { state, commit },
    { agent, name = 'default', messageId, turnId, rating }
  ) {
    const key = buildKey(agent, name);
    const client = state.connections[key];
    if (!client) return;

    // Send via WebSocket
    client.send(
      JSON.stringify({
        type: 'cf_agent_feedback',
        messageId,
        turnId,
        rating,
      })
    );

    // Optimistic update
    commit('setMessageFeedback', { key, messageId, feedback: rating });
  },

  // ============================================================================
  // Backend Agents Actions
  // ============================================================================

  /**
   * Fetch all agents from the ConveYour backend
   * @param {boolean} [force=false] - Force reload even if already loaded
   */
  async fetchBackendAgents({ state, commit }, { force = false } = {}) {
    // Skip if already loaded and not forcing
    if (state.backendAgentsLoaded && !force) {
      return state.backendAgents;
    }

    // Skip if currently loading
    if (state.backendAgentsLoading) {
      return state.backendAgents;
    }

    commit('setBackendAgentsLoading', true);

    try {
      const res = await agentsResource.get();
      if (res.ok) {
        const agents = res.data?.results || res.data || [];
        commit('setBackendAgents', agents);
        commit('setBackendAgentsLoaded', true);
        return agents;
      }
    } catch (err) {
      console.error('Failed to fetch backend agents:', err);
      throw err;
    } finally {
      commit('setBackendAgentsLoading', false);
    }

    return [];
  },

  /**
   * Create a new agent in the ConveYour backend
   * @param {string} name - The display name for the agent
   * @param {string} type - The agent type ('chat' or 'page-builder')
   * @param {string} uuid - The Cloudflare agent UUID (instanceName)
   */
  async createBackendAgent({ commit }, { name, type, uuid }) {
    try {
      const res = await agentsResource.create({ name, type, uuid });
      if (res.ok && res.data) {
        commit('addBackendAgent', res.data);
        return res.data;
      }
    } catch (err) {
      console.error('Failed to create backend agent:', err);
      throw err;
    }

    return null;
  },

  /**
   * Update an agent in the ConveYour backend
   * @param {string} id - The agent's MongoDB id
   * @param {object} updates - The fields to update
   */
  async updateBackendAgentRecord({ commit }, { id, ...updates }) {
    try {
      const res = await agentsResource.update(id, updates);
      if (res.ok && res.data) {
        commit('updateBackendAgent', res.data);
        return res.data;
      }
    } catch (err) {
      console.error('Failed to update backend agent:', err);
      throw err;
    }

    return null;
  },

  /**
   * Delete an agent from the ConveYour backend
   * @param {string} id - The agent's MongoDB id
   */
  async deleteBackendAgent({ commit }, { id }) {
    try {
      const res = await agentsResource.delete(id);
      if (res.ok) {
        commit('removeBackendAgent', id);
        return true;
      }
    } catch (err) {
      console.error('Failed to delete backend agent:', err);
      throw err;
    }

    return false;
  },

  /**
   * Find a backend agent by its UUID
   * @param {string} uuid - The Cloudflare agent UUID to find
   */
  getBackendAgentByUuid({ state }, uuid) {
    return state.backendAgents.find((a) => a.uuid === uuid) || null;
  },

  /**
   * Update the display name of an agent in both the local registry and backend
   * @param {string} agent - The agent type
   * @param {string} name - The instance name (UUID)
   * @param {string} displayName - The new display name
   */
  async updateAgentDisplayName(
    { state, commit, dispatch },
    { agent, name, displayName }
  ) {
    const key = buildKey(agent, name);

    // Update local instance registry
    commit('updateInstanceDisplayName', { key, displayName });

    // Wait for backend agent to be available (may be in-flight from connect)
    // Retry up to 10 times with 200ms delay (2 seconds total)
    let backendAgent = null;
    let attempts = 0;
    const maxAttempts = 10;

    while (!backendAgent && attempts < maxAttempts) {
      backendAgent = state.backendAgents.find((a) => a.uuid === name);
      if (!backendAgent) {
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }

    // Update backend agent if found
    if (backendAgent) {
      try {
        await dispatch('updateBackendAgentRecord', {
          id: backendAgent.id,
          name: displayName,
        });
      } catch (err) {
        console.warn('Failed to update backend agent display name:', err);
      }
    } else {
      console.warn(
        'Backend agent not found for UUID:',
        name,
        '- display name not synced'
      );
    }
  },
};

const getters = {
  // Get agent data by agent type and instance name
  // Usage: this.$store.getters['agents/agent']('customer-support', 'default')
  agent:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      return {
        key,
        connected: state.connectionStatus[key] === 'connected',
        connecting: state.connectionStatus[key] === 'connecting',
        disconnected:
          !state.connectionStatus[key] ||
          state.connectionStatus[key] === 'disconnected',
        messages: state.messages[key] || [],
        agentState: state.agentStates[key] || {},
        loading: state.loading[key] || false,
      };
    },

  isConnected:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      return state.connectionStatus[key] === 'connected';
    },

  allConnectedAgents: (state) =>
    Object.keys(state.connections).filter(
      (key) => state.connectionStatus[key] === 'connected'
    ),

  // Get the agents host from environment
  agentsHost: () => vueEnv('agents_host'),

  /**
   * Check if any message has active (pending) tool calls
   * Useful for showing "processing" indicators in the UI
   */
  hasActiveToolCalls:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      const messages = state.messages[key] || [];
      return messages.some((m) =>
        m.toolCalls?.some((tc) => tc.status === 'pending')
      );
    },

  /**
   * Get the last error from the message list
   * Returns null if no errors exist
   */
  lastError:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      const messages = state.messages[key] || [];
      const errorMsg = [...messages].reverse().find((m) => m.error);
      return errorMsg?.error || null;
    },

  /**
   * Get the current streaming message (if any)
   * Returns null if no message is currently streaming
   */
  streamingMessage:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      const messages = state.messages[key] || [];
      return messages.find((m) => m.status === 'streaming') || null;
    },

  /**
   * Get all tool calls from all messages for an agent
   * Useful for debugging or showing tool call history
   */
  allToolCalls:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      const messages = state.messages[key] || [];
      return messages.flatMap((m) => m.toolCalls || []);
    },

  // ============================================================================
  // Data Events / Page Builder Getters
  // ============================================================================

  /**
   * Get all data events for an agent (the back-channel events)
   * Useful for components that need to react to page builder or other data events
   */
  dataEvents:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      return state.dataEvents[key] || [];
    },

  /**
   * Get the active page build info for an agent
   * Returns { pageId, description, status, error?, operationCount? } or null
   */
  activePageBuild:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      return state.activePageBuild[key] || null;
    },

  /**
   * Check if a page is currently being built for an agent
   */
  isPageBuilding:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      return state.activePageBuild[key]?.status === 'building';
    },

  /**
   * Check if a page build completed successfully for an agent
   */
  isPageComplete:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      return state.activePageBuild[key]?.status === 'complete';
    },

  /**
   * Check if a page build errored for an agent
   */
  isPageError:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      return state.activePageBuild[key]?.status === 'error';
    },

  /**
   * Get the current page operation count for an agent
   */
  pageOperationCount:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      return state.activePageBuild[key]?.operationCount || 0;
    },

  // ============================================================================
  // Instance Registry Getters
  // ============================================================================

  /**
   * Get all chat agent instances, sorted by createdAt descending (newest first)
   * Filters out page-builder and other non-chat agent types
   */
  chatInstances: (state) => {
    return Object.entries(state.instanceRegistry)
      .filter(([, instance]) => instance.agentType === 'chat')
      .map(([key, instance]) => ({
        key,
        ...instance,
      }))
      .sort((a, b) => b.createdAt - a.createdAt);
  },

  /**
   * Get instance metadata by key
   */
  getInstance:
    (state) =>
    (agent, name = 'default') => {
      const key = buildKey(agent, name);
      return state.instanceRegistry[key] || null;
    },

  // ============================================================================
  // Backend Agents Getters
  // ============================================================================

  /**
   * Get all backend agents
   */
  allBackendAgents: (state) => state.backendAgents,

  /**
   * Get all chat-type backend agents, sorted by created_at descending
   * Note: created_at is a Unix timestamp in seconds from the API
   */
  chatBackendAgents: (state) => {
    return state.backendAgents
      .filter((a) => a.type === 'chat')
      .sort((a, b) => {
        // created_at is Unix timestamp in seconds, compare directly (no conversion needed for sorting)
        const timestampA = a.created_at || 0;
        const timestampB = b.created_at || 0;
        return timestampB - timestampA; // Newest first
      });
  },

  /**
   * Get a backend agent by its UUID
   */
  backendAgentByUuid: (state) => (uuid) => {
    return state.backendAgents.find((a) => a.uuid === uuid) || null;
  },

  /**
   * Get a backend agent by its MongoDB id
   */
  backendAgentById: (state) => (id) => {
    return state.backendAgents.find((a) => a.id === id) || null;
  },

  /**
   * Check if backend agents are loading
   */
  backendAgentsLoading: (state) => state.backendAgentsLoading,

  /**
   * Check if backend agents have been loaded
   */
  backendAgentsLoaded: (state) => state.backendAgentsLoaded,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
